import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  Empty,
  Spin,
  Tag,
  Tooltip
} from 'antd';
import {
  FileOutlined,
  DownloadOutlined,
  CalendarOutlined,
  EuroOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import partnerService from '../../../services/partnerService';
import BackButton from '../../../components/BackButton';
import './PartnerInvoices.scss';

const { Title, Text } = Typography;

const PartnerInvoices = observer(() => {
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    const result = await partnerService.fetchMyInvoices();
    if (result.success) {
      setInvoices(result.data.results || result.data);
    }
    setLoading(false);
  };

  const columns = [
    {
      title: t('partner.invoices.titleColumn'),
      dataIndex: 'title',
      key: 'title',
      width: 200,
      ellipsis: true,
      render: (title) => (
        <Tooltip title={title}>
          <Space style={{ maxWidth: '100%' }}>
            <FileOutlined />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
          </Space>
        </Tooltip>
      )
    },
    {
      title: t('partner.invoices.invoiceNumber'),
      dataIndex: 'invoice_number',
      key: 'invoice_number',
      width: 120,
      ellipsis: true,
      render: (num) => num || '-'
    },
    {
      title: t('partner.invoices.amount'),
      dataIndex: 'amount',
      key: 'amount',
      width: 100,
      render: (amount) => amount ? (
        <Text strong style={{ color: '#52c41a' }}>
          <EuroOutlined style={{ marginRight: 4 }} />
          {parseFloat(amount).toFixed(2)}
        </Text>
      ) : '-'
    },
    {
      title: t('partner.invoices.invoiceDate'),
      dataIndex: 'invoice_date',
      key: 'invoice_date',
      width: 130,
      sorter: (a, b) => new Date(a.invoice_date) - new Date(b.invoice_date),
      render: (date) => (
        <Space>
          <CalendarOutlined />
          <span>{date}</span>
        </Space>
      )
    },
    {
      title: t('partner.invoices.period'),
      key: 'period',
      width: 180,
      ellipsis: true,
      render: (_, record) => {
        if (record.period_start && record.period_end) {
          const period = `${record.period_start} ${t('partner.invoices.to')} ${record.period_end}`;
          return (
            <Tooltip title={period}>
              <span>{period}</span>
            </Tooltip>
          );
        }
        return '-';
      }
    },
    {
      title: t('common.download'),
      key: 'download',
      width: 140,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<DownloadOutlined />}
          href={record.file_url}
          target="_blank"
        >
          PDF
        </Button>
      )
    }
  ];

  if (loading) {
    return (
      <div className="partner-invoices-page loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="partner-invoices-page">
      <BackButton to="/partner/dashboard" label={t('partner.backToDashboard')} />

      <div className="page-header" style={{ marginBottom: 24 }}>
        <Title level={2}>
          <FileOutlined style={{ marginRight: 12 }} />
          {t('partner.invoices.title')}
        </Title>
        <Text type="secondary">
          {t('partner.invoices.description')}
        </Text>
      </div>

      <Card>
        {invoices.length === 0 ? (
          <Empty
            description={t('partner.invoices.noInvoices')}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={invoices}
            rowKey="id"
            scroll={{ x: 870 }}
            pagination={{ pageSize: 10 }}
          />
        )}
      </Card>
    </div>
  );
});

export default PartnerInvoices;
